import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Plus, Pencil, Trash2, ChevronRight, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { Department, PayrollPosition } from "@/types/payroll-admin";

interface PositionsTabProps {
  departments: Department[];
  positionsByDepartment: {
    department: Department;
    positions: PayrollPosition[];
  }[];
  onAddPosition: (
    position: Omit<PayrollPosition, "id" | "created_at" | "updated_at">,
  ) => void;
  onUpdatePosition: (id: string, updates: Partial<PayrollPosition>) => void;
  onDeletePosition: (id: string) => void;
  onAddDepartment: (
    department: Omit<Department, "id" | "created_at" | "updated_at">,
  ) => void;
  onUpdateDepartment: (id: string, updates: Partial<Department>) => void;
  onDeleteDepartment: (id: string) => void;
}

const MAX_POSITIONS_PER_DEPARTMENT = 7;

const emptyPositionForm = {
  name: "",
  department_id: "",
  description: "",
};

export function PositionsTab({
  departments,
  positionsByDepartment,
  onAddPosition,
  onUpdatePosition,
  onDeletePosition,
}: PositionsTabProps) {
  const [formData, setFormData] = useState(emptyPositionForm);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingPosition, setEditingPosition] =
    useState<PayrollPosition | null>(null);

  const positionsCountByDept = positionsByDepartment.reduce(
    (acc, { department, positions }) => {
      acc[department.id] = positions.length;
      return acc;
    },
    {} as Record<string, number>,
  );

  const selectedDeptCount = formData.department_id
    ? (positionsCountByDept[formData.department_id] ?? 0)
    : 0;
  const isSelectedDeptAtLimit =
    !editingPosition && selectedDeptCount >= MAX_POSITIONS_PER_DEPARTMENT;

  const handleAddPosition = () => {
    if (!formData.name || !formData.department_id) {
      toast.error("Please fill in all required fields");
      return;
    }

    const deptCount = positionsCountByDept[formData.department_id] ?? 0;
    if (deptCount >= MAX_POSITIONS_PER_DEPARTMENT) {
      toast.error(
        `Maximum ${MAX_POSITIONS_PER_DEPARTMENT} positions allowed per department`,
      );
      return;
    }

    onAddPosition({
      ...formData,
      display_order: 99,
      is_active: true,
    });

    resetForm();
    setIsDialogOpen(false);
    toast.success("Position added");
  };

  const handleEditPosition = (position: PayrollPosition) => {
    setFormData({
      name: position.name,
      department_id: position.department_id,
      description: position.description,
    });
    setEditingPosition(position);
    setIsDialogOpen(true);
  };

  const handleSavePosition = () => {
    if (!editingPosition) return;

    if (!formData.name || !formData.department_id) {
      toast.error("Please fill in all required fields");
      return;
    }

    onUpdatePosition(editingPosition.id, {
      name: formData.name,
      description: formData.description,
    });

    resetForm();
    setEditingPosition(null);
    setIsDialogOpen(false);
    toast.success("Position updated");
  };

  const resetForm = () => {
    setFormData(emptyPositionForm);
  };

  const handleFormChange = (field: keyof typeof formData, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const renderPositionForm = (isEditMode: boolean = false) => (
    <>
      <div className="space-y-2">
        <Label htmlFor="position-name">Position Name *</Label>
        <Input
          id="position-name"
          value={formData.name}
          onChange={(e) => handleFormChange("name", e.target.value)}
          placeholder="e.g., Front Desk Agent"
        />
      </div>

      {!isEditMode ? (
        <div className="space-y-2">
          <Label htmlFor="position-department">Department *</Label>
          <Select
            value={formData.department_id}
            onValueChange={(value) => handleFormChange("department_id", value)}
          >
            <SelectTrigger
              id="position-department"
              className="flex items-center gap-2"
            >
              {formData.department_id ? (
                <div className="flex items-center gap-2 w-full">
                  <div
                    className="w-3 h-3 rounded-full flex-shrink-0"
                    style={{
                      backgroundColor:
                        departments.find((d) => d.id === formData.department_id)
                          ?.color || "#ccc",
                    }}
                  />
                  <span className="truncate">
                    {departments.find((d) => d.id === formData.department_id)
                      ?.name || "Select department"}
                  </span>
                </div>
              ) : (
                <span className="text-muted-foreground">Select department</span>
              )}
            </SelectTrigger>
            <SelectContent>
              {departments.map((dept) => {
                const count = positionsCountByDept[dept.id] ?? 0;
                const atLimit = count >= MAX_POSITIONS_PER_DEPARTMENT;
                return (
                  <SelectItem key={dept.id} value={dept.id}>
                    <div className="flex items-center gap-2 w-full">
                      <div
                        className="w-3 h-3 rounded-full flex-shrink-0"
                        style={{ backgroundColor: dept.color }}
                      />
                      <span className={atLimit ? "text-muted-foreground" : ""}>
                        {dept.name}
                      </span>
                      <span
                        className={`ml-auto text-xs px-1.5 py-0.5 rounded-full ${
                          atLimit
                            ? "bg-destructive/10 text-destructive"
                            : "bg-muted text-muted-foreground"
                        }`}
                      >
                        {count}/{MAX_POSITIONS_PER_DEPARTMENT}
                      </span>
                    </div>
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>

          {isSelectedDeptAtLimit && (
            <div className="flex items-center gap-2 text-sm text-destructive">
              <AlertCircle className="h-4 w-4 shrink-0" />
              This department has reached the maximum of{" "}
              {MAX_POSITIONS_PER_DEPARTMENT} positions.
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          <Label>Department</Label>
          <div className="flex items-center gap-2 p-2 border rounded-md bg-muted/50">
            <div
              className="w-3 h-3 rounded-full flex-shrink-0"
              style={{
                backgroundColor:
                  departments.find((d) => d.id === formData.department_id)
                    ?.color || "#ccc",
              }}
            />
            <span className="text-sm">
              {departments.find((d) => d.id === formData.department_id)?.name ||
                "Unknown Department"}
            </span>
            <span className="text-xs text-muted-foreground ml-auto">
              (Department cannot be changed when editing)
            </span>
          </div>
        </div>
      )}

      <div className="space-y-2">
        <Label htmlFor="position-description">Description</Label>
        <Input
          id="position-description"
          value={formData.description}
          onChange={(e) => handleFormChange("description", e.target.value)}
          placeholder="Brief description of the role"
        />
      </div>
    </>
  );

  const sortedPositionsByDepartment = [...positionsByDepartment].sort(
    (a, b) => a.department.display_order - b.department.display_order,
  );

  const totalPositions = positionsByDepartment.reduce(
    (acc, curr) => acc + curr.positions.length,
    0,
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold">Departments & Positions</h2>
          <p className="text-sm text-muted-foreground mt-1">
            {departments.length} departments, {totalPositions} active positions
          </p>
        </div>
        <Button
          className="bg-primary-blue text-white"
          onClick={() => setIsDialogOpen(true)}
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Position
        </Button>
      </div>

      {sortedPositionsByDepartment.map(({ department, positions }) => {
        const isAtLimit = positions.length >= MAX_POSITIONS_PER_DEPARTMENT;

        return (
          <Card key={department.id}>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: department.color }}
                  />
                  <div>
                    <CardTitle className="text-lg">{department.name}</CardTitle>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span
                    className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                      isAtLimit
                        ? "bg-destructive/10 text-destructive"
                        : "bg-muted text-muted-foreground"
                    }`}
                  >
                    {positions.length}/{MAX_POSITIONS_PER_DEPARTMENT}
                  </span>
                  <Badge variant="secondary" className="font-normal">
                    {positions.length}{" "}
                    {positions.length === 1 ? "position" : "positions"}
                  </Badge>
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                </div>
              </div>

              {isAtLimit && (
                <div className="flex items-center gap-2 mt-2 px-3 py-2 rounded-md bg-destructive/5 border border-destructive/20 text-sm text-destructive">
                  <AlertCircle className="h-4 w-4 shrink-0" />
                  Maximum {MAX_POSITIONS_PER_DEPARTMENT} positions reached.
                  Delete a position to add a new one.
                </div>
              )}
            </CardHeader>
            <CardContent>
              {positions.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Position</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {positions.map((position) => (
                      <TableRow key={position.id}>
                        <TableCell className="font-medium">
                          <div className="flex items-center gap-2">
                            <div
                              className="w-2 h-2 rounded-full"
                              style={{ backgroundColor: department.color }}
                            />
                            {position.name}
                          </div>
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {position.description || "No description"}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEditPosition(position)}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => onDeletePosition(position.id)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-6">
                  <p className="text-muted-foreground">
                    No positions in this department yet
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-2"
                    onClick={() => {
                      setFormData({
                        ...emptyPositionForm,
                        department_id: department.id,
                      });
                      setIsDialogOpen(true);
                    }}
                  >
                    <Plus className="h-3 w-3 mr-1" />
                    Add first position
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}

      <Dialog
        open={isDialogOpen}
        onOpenChange={(open) => {
          setIsDialogOpen(open);
          if (!open) {
            resetForm();
            setEditingPosition(null);
          }
        }}
      >
        <DialogContent className="max-w-lg max-h-[90vh] p-0 overflow-hidden flex flex-col">
          <DialogHeader className="px-6 pt-6 pb-4 border-b">
            <DialogTitle>
              {editingPosition ? "Edit Position" : "Add New Position"}
            </DialogTitle>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto px-6 pb-6 minimal-scrollbar">
            <div className="space-y-4 py-4">
              {renderPositionForm(!!editingPosition)}
            </div>
          </div>

          <div className="px-6 py-4 shrink-0">
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setIsDialogOpen(false);
                  resetForm();
                  setEditingPosition(null);
                }}
              >
                Cancel
              </Button>
              <Button
                className="bg-primary-blue text-white"
                disabled={!editingPosition && isSelectedDeptAtLimit}
                onClick={
                  editingPosition ? handleSavePosition : handleAddPosition
                }
              >
                {editingPosition ? "Save" : "Add Position"}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
